import { create } from 'zustand';
import { createStorage } from '@/shared/storage';
import { getTabId, getWindowId } from './ids';
import type { ChatLoginScope, ThreadLockState } from './types';

const storage = createStorage('local');

const LOCK_PREFIX = 'agenthub:chat-locks:v1:';
const LOCK_BROADCAST_PREFIX = 'agenthub:chat-locks-sync:';
const HEARTBEAT_INTERVAL_MS = 5_000;
const LOCK_STALE_AFTER_MS = HEARTBEAT_INTERVAL_MS * 3;

type LockSnapshot = {
  version: 1;
  userId: string;
  loginSessionId: string;
  locks: Record<string, ThreadLockState>;
};

type ChatLockState = LockSnapshot & {
  isHydrated: boolean;
};

type ChatLockActions = {
  configureSession: (scope: ChatLoginScope) => void;
  clearSession: () => void;
  acquireLock: (threadId: string) => void;
  releaseLock: (threadId: string) => void;
  releaseAllLocks: () => void;
  heartbeatLock: (threadId: string) => void;
  clearAll: () => void;
};

type LockStore = ChatLockState & ChatLockActions;

type RuntimeState = {
  scope: ChatLoginScope | null;
  key: string | null;
  channel: BroadcastChannel | null;
  timer: number | null;
  storageListenerInstalled: boolean;
};

const runtime: RuntimeState = {
  scope: null,
  key: null,
  channel: null,
  timer: null,
  storageListenerInstalled: false,
};

function nowIso(): string {
  return new Date().toISOString();
}

function emptySnapshot(scope: ChatLoginScope): LockSnapshot {
  return {
    version: 1,
    userId: scope.userId,
    loginSessionId: scope.loginSessionId,
    locks: {},
  };
}

function lockStorageKey(scope: ChatLoginScope): string {
  return `${LOCK_PREFIX}${scope.userId}:${scope.loginSessionId}`;
}

function lockChannel(scope: ChatLoginScope): string {
  return `${LOCK_BROADCAST_PREFIX}${scope.userId}:${scope.loginSessionId}`;
}

function isLockSnapshot(value: unknown): value is LockSnapshot {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return record.version === 1
    && typeof record.userId === 'string'
    && typeof record.loginSessionId === 'string'
    && typeof record.locks === 'object'
    && record.locks !== null;
}

function readSnapshot(scope: ChatLoginScope): LockSnapshot {
  const raw = storage.get<unknown>(lockStorageKey(scope), null);
  return isLockSnapshot(raw) ? raw : emptySnapshot(scope);
}

function persistSnapshot(snapshot: LockSnapshot): void {
  storage.set(lockStorageKey(snapshot), snapshot);
}

function writeSnapshot(snapshot: LockSnapshot): void {
  persistSnapshot(snapshot);
  runtime.channel?.postMessage(snapshot);
}

function isOwnedByCurrentTab(lock: ThreadLockState): boolean {
  return lock.ownerTabId === getTabId() && lock.ownerWindowId === getWindowId();
}

function isStaleLock(lock: ThreadLockState): boolean {
  const heartbeatMs = Date.parse(lock.heartbeatAt);
  if (!Number.isFinite(heartbeatMs)) return true;
  return Date.now() - heartbeatMs > LOCK_STALE_AFTER_MS;
}

function attachBroadcast(scope: ChatLoginScope): void {
  runtime.channel?.close();
  runtime.channel = null;
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(lockChannel(scope));
  channel.onmessage = (event: MessageEvent<unknown>) => {
    const snapshot = event.data;
    if (!isLockSnapshot(snapshot)) return;
    useChatLockStore.setState((state) => ({ ...state, ...snapshot, isHydrated: true }));
  };
  runtime.channel = channel;

  if (!runtime.storageListenerInstalled && typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (!runtime.key || event.key !== runtime.key || !runtime.scope) return;
      const next = event.newValue === null
        ? emptySnapshot(runtime.scope)
        : readSnapshot(runtime.scope);
      useChatLockStore.setState((state) => ({ ...state, ...next, isHydrated: true }));
    });
    runtime.storageListenerInstalled = true;
  }
}

function restartHeartbeat(): void {
  if (runtime.timer !== null && typeof window !== 'undefined') {
    window.clearInterval(runtime.timer);
    runtime.timer = null;
  }

  if (typeof window === 'undefined' || !runtime.scope) return;
  runtime.timer = window.setInterval(() => {
    const state = useChatLockStore.getState();
    const tabId = getTabId();
    for (const lock of Object.values(state.locks)) {
      if (lock.ownerTabId === tabId) {
        state.heartbeatLock(lock.threadId);
      }
    }
  }, HEARTBEAT_INTERVAL_MS);
}

export const useChatLockStore = create<LockStore>((set) => ({
  ...emptySnapshot({ userId: '', loginSessionId: '' }),
  userId: '',
  loginSessionId: '',
  isHydrated: false,

  configureSession: (scope) => {
    runtime.scope = scope;
    runtime.key = lockStorageKey(scope);
    attachBroadcast(scope);
    const snapshot = readSnapshot(scope);
    persistSnapshot(snapshot);
    restartHeartbeat();
    set({
      ...snapshot,
      isHydrated: true,
    });
  },

  clearSession: () => {
    if (runtime.key) storage.remove(runtime.key);
    runtime.channel?.close();
    runtime.channel = null;
    if (runtime.timer !== null && typeof window !== 'undefined') {
      window.clearInterval(runtime.timer);
      runtime.timer = null;
    }
    runtime.scope = null;
    runtime.key = null;
    set({
      ...emptySnapshot({ userId: '', loginSessionId: '' }),
      userId: '',
      loginSessionId: '',
      isHydrated: false,
    });
  },

  acquireLock: (threadId) => {
    set((state) => {
      const scope = runtime.scope;
      if (!scope) return state;
      const snapshot = readSnapshot(scope);
      const existing = snapshot.locks[threadId];
      if (existing && !isOwnedByCurrentTab(existing) && !isStaleLock(existing)) {
        return {
          ...state,
          ...snapshot,
        };
      }
      const timestamp = nowIso();
      const next: LockSnapshot = {
        ...snapshot,
        locks: {
          ...snapshot.locks,
          [threadId]: {
            threadId,
            userId: scope.userId,
            loginSessionId: scope.loginSessionId,
            ownerTabId: getTabId(),
            ownerWindowId: getWindowId(),
            acquiredAt: existing && isOwnedByCurrentTab(existing) ? existing.acquiredAt : timestamp,
            heartbeatAt: timestamp,
          },
        },
      };
      writeSnapshot(next);
      return {
        ...state,
        ...next,
      };
    });
  },

  releaseLock: (threadId) => {
    set((state) => {
      const scope = runtime.scope;
      if (!scope) return state;
      const snapshot = readSnapshot(scope);
      const existing = snapshot.locks[threadId];
      if (!existing || !isOwnedByCurrentTab(existing)) {
        return {
          ...state,
          ...snapshot,
        };
      }
      const nextLocks = { ...snapshot.locks };
      delete nextLocks[threadId];
      const next: LockSnapshot = { ...snapshot, locks: nextLocks };
      writeSnapshot(next);
      return {
        ...state,
        ...next,
      };
    });
  },

  releaseAllLocks: () => {
    set((state) => {
      const scope = runtime.scope;
      if (!scope) return state;
      const tabId = getTabId();
      const windowId = getWindowId();
      const snapshot = readSnapshot(scope);
      const nextLocks = { ...snapshot.locks };
      for (const [threadId, lock] of Object.entries(nextLocks)) {
        if (lock.ownerTabId === tabId && lock.ownerWindowId === windowId) delete nextLocks[threadId];
      }
      const next: LockSnapshot = { ...snapshot, locks: nextLocks };
      writeSnapshot(next);
      return {
        ...state,
        ...next,
      };
    });
  },

  heartbeatLock: (threadId) => {
    set((state) => {
      const scope = runtime.scope;
      if (!scope) return state;
      const snapshot = readSnapshot(scope);
      const existing = snapshot.locks[threadId];
      if (!existing || !isOwnedByCurrentTab(existing)) {
        return {
          ...state,
          ...snapshot,
        };
      }
      const next: LockSnapshot = {
        ...snapshot,
        locks: {
          ...snapshot.locks,
          [threadId]: {
            ...existing,
            heartbeatAt: nowIso(),
          },
        },
      };
      writeSnapshot(next);
      return {
        ...state,
        ...next,
      };
    });
  },

  clearAll: () => {
    set((state) => {
      const scope = runtime.scope;
      if (!scope) return state;
      const next = emptySnapshot(scope);
      writeSnapshot(next);
      return {
        ...state,
        ...next,
      };
    });
  },
}));
